import { Box, Tooltip, Typography } from '@mui/material';
import { VisibilityOutlined, ArrowBack } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAppSelector } from '@/app/store';
import { selectAuth } from '@/app/features/authSlice';
import { useLogoutMutation } from '@/app/services/authApi';
import { osirisTokens } from '@/theme/tokens';
import { Breadcrumbs } from './Breadcrumbs';

function formatUtcTime(d: Date): string {
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const ss = String(d.getUTCSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss} UTC`;
}

function formatDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = d.getFullYear();
  return `${mm}/${dd}/${yy}`;
}

const utilityCellSx = {
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: 2,
  borderLeft: `1px solid ${osirisTokens.border.primary}`,
};

const utilityTextSx = {
  fontFamily: osirisTokens.fonts.mono,
  fontWeight: 500,
  fontSize: 14,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: osirisTokens.text.secondary,
  whiteSpace: 'nowrap',
};

export function TopBar() {
  const { user, impersonation } = useAppSelector(selectAuth);
  const [now, setNow] = useState(() => new Date());
  const [isHoveringUser, setIsHoveringUser] = useState(false);
  const navigate = useNavigate();
  const [logoutMutation] = useLogoutMutation();

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const handleExitImpersonation = async () => {
    try {
      await logoutMutation().unwrap();
      navigate('/admin/users');
    } catch (error) {
      console.error('Failed to exit impersonation:', error);
    }
  };

  return (
    <Box
      className="topbar"
      sx={{
        gridArea: 'topbar',
        height: osirisTokens.layout.headerHeight,
        bgcolor: osirisTokens.bg.page,
        borderBottom: `1px solid ${osirisTokens.border.primary}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 2,
      }}
    >
      <Breadcrumbs />

      <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
        {/* Weather (placeholder) */}
        {/* <Box className="topbar-weather" sx={utilityCellSx}>
          <WbCloudyOutlined sx={{ fontSize: 20, color: osirisTokens.icons.primary }} />
          <Typography sx={utilityTextSx}>24°C</Typography>
        </Box> */}

        {/* UTC clock */}
        <Box className="topbar-clock" sx={utilityCellSx}>
          <Typography sx={utilityTextSx}>{formatUtcTime(now)}</Typography>
        </Box>

        {/* Date */}
        <Box className="topbar-date" sx={utilityCellSx}>
          <Typography sx={utilityTextSx}>{formatDate(now)}</Typography>
        </Box>

        {/* User + role (impersonation-aware) */}
        <Tooltip
          title={
            impersonation
              ? isHoveringUser
                ? 'Click to exit impersonation'
                : 'Impersonation active — you are viewing as another user'
              : ''
          }
          disableHoverListener={!impersonation}
        >
          <Box
            className={`topbar-user${impersonation ? ' is-impersonating' : ''}`}
            onClick={impersonation ? handleExitImpersonation : undefined}
            onMouseEnter={() => impersonation && setIsHoveringUser(true)}
            onMouseLeave={() => setIsHoveringUser(false)}
            sx={{
              ...utilityCellSx,
              position: 'relative',
              // default state has no fill; hover = yellow 10%
              // (Figma To Do 28751:28366 / 28751:28357)
              ...(impersonation && {
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(236, 173, 0, 0.1)' },
              }),
            }}
          >
            {impersonation && (
              <VisibilityOutlined
                sx={{
                  fontSize: 18,
                  color: osirisTokens.state.impersonation,
                }}
              />
            )}
            <Typography
              sx={{
                ...utilityTextSx,
                ...(impersonation && {
                  color: osirisTokens.state.impersonation,
                  fontWeight: 600,
                  textDecoration: 'underline',
                  textUnderlineOffset: '4px',
                }),
              }}
            >
              {user?.name ?? 'Guest'}
              {user?.role && (
                <Box
                  component="span"
                  sx={{
                    color: impersonation
                      ? osirisTokens.state.impersonation
                      : osirisTokens.text.muted,
                    opacity: impersonation ? 0.75 : 1,
                  }}
                >
                  {impersonation ? '·' : ' | '}
                  {user.role}
                </Box>
              )}
            </Typography>
            {impersonation && isHoveringUser && (
              <Box
                className="topbar-exit-impersonation"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  // opaque page base + yellow 10% tint (Figma To Do 28751:28357)
                  background: `linear-gradient(rgba(236, 173, 0, 0.1), rgba(236, 173, 0, 0.1)), ${osirisTokens.bg.page}`,
                }}
              >
                <ArrowBack sx={{ fontSize: 18, color: osirisTokens.state.impersonation }} />
                <Typography
                  sx={{
                    ...utilityTextSx,
                    color: osirisTokens.state.impersonation,
                    fontWeight: 600,
                  }}
                >
                  Exit
                </Typography>
              </Box>
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}
